<template>
  <div class="picker">
    <div v-if="selectedOptions.length" class="picker-chips" role="list" :aria-label="label + ' selected'">
      <span v-for="option in selectedOptions" :key="option.id" class="picker-chip" role="listitem">
        <aha-tag
          bordered
          closable
          :color="errors[option.id] ? 'error' : 'neutral'"
          :title="errors[option.id] || ''"
          @close="remove(option.id)"
        >{{ option.label }} · {{ option.sub }}</aha-tag>
      </span>
    </div>

    <aha-input
      :aria-label="'Search ' + label"
      :placeholder="placeholder"
      :value="query"
      :disabled.prop="disabled"
      @input="query = detailValue($event)"
    ></aha-input>

    <ul class="picker-list" :aria-label="label">
      <li v-for="option in filteredOptions" :key="option.id">
        <button
          type="button"
          class="picker-option"
          :class="{ 'picker-option-selected': isSelected(option.id) }"
          :aria-pressed="isSelected(option.id) ? 'true' : 'false'"
          :disabled="disabled || isAttached(option.id)"
          @click="toggle(option.id)"
        >
          <span class="picker-check">
            <aha-icon v-if="isSelected(option.id)" name="system-check" size="14" decorative></aha-icon>
          </span>
          <span class="picker-text">
            <span class="picker-name">{{ option.label }}</span>
            <span class="picker-sub">{{ option.sub }}</span>
          </span>
          <aha-status-badge v-if="isAttached(option.id)" size="sm" look="quiet">Attached</aha-status-badge>
        </button>
      </li>
      <li v-if="!filteredOptions.length" class="picker-empty">{{ options.length ? 'No match.' : 'Nothing to pick.' }}</li>
    </ul>

    <ul v-if="errorRows.length" class="picker-errors" role="alert">
      <li v-for="row in errorRows" :key="row.id"><strong>{{ row.label }}</strong>: {{ row.message }}</li>
    </ul>
  </div>
</template>

<script>
import nativeAhaElements from './nativeAhaElements'

export default {
  name: 'StripeBridgeMultiPicker',
  components: { ...nativeAhaElements },
  props: {
    label: { type: String, required: true },
    placeholder: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    attachedIds: { type: Array, default: () => [] },
    value: { type: Array, default: () => [] },
    errors: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return { query: '' }
  },
  computed: {
    optionById() {
      return Object.fromEntries(this.options.map(o => [o.id, o]))
    },
    selectedOptions() {
      return this.value.map(id => this.optionById[id]).filter(Boolean)
    },
    filteredOptions() {
      const needle = this.query.trim().toLowerCase()
      if (!needle) return this.options
      return this.options.filter(o => [o.label, o.sub].some(text => String(text).toLowerCase().includes(needle)))
    },
    errorRows() {
      return this.selectedOptions
        .filter(o => this.errors[o.id])
        .map(o => ({ id: o.id, label: o.label, message: this.errors[o.id] }))
    },
  },
  methods: {
    detailValue(event) {
      return event.detail && typeof event.detail.value === 'string' ? event.detail.value : this.query
    },
    isSelected(id) {
      return this.value.includes(id)
    },
    isAttached(id) {
      return this.attachedIds.includes(id)
    },
    toggle(id) {
      if (this.isAttached(id)) return
      this.$emit('input', this.isSelected(id) ? this.value.filter(v => v !== id) : [...this.value, id])
    },
    remove(id) {
      if (!this.disabled) this.$emit('input', this.value.filter(v => v !== id))
    },
  },
}
</script>

<style lang="scss" scoped>
.picker {
  display: flex;
  flex-direction: column;
  gap: var(--aha-space-8);
}

.picker > aha-input {
  display: block;
  width: 100%;
}

.picker-chips {
  display: flex;
  flex-wrap: wrap;
  gap: var(--aha-space-8);
}

.picker-list,
.picker-errors {
  margin: 0;
  padding: 0;
  list-style: none;
}

.picker-list {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid var(--aha-border-default);
  border-radius: var(--aha-radius-default);
  background: var(--aha-bg-container);
}

.picker-option {
  display: flex;
  align-items: center;
  gap: var(--aha-space-8);
  width: 100%;
  padding: var(--aha-space-8) var(--aha-space-12);
  border: 0;
  background: transparent;
  color: var(--aha-text-default);
  font: inherit;
  font-size: var(--aha-size-default);
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: var(--aha-bg-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--aha-border-focus);
    outline-offset: -2px;
  }

  &:disabled {
    color: var(--aha-text-secondary);
    cursor: not-allowed;
  }
}

.picker-option-selected {
  background: var(--aha-bg-accent);
}

.picker-check {
  display: inline-flex;
  flex: 0 0 16px;
  justify-content: center;
  color: var(--aha-color-primary);
}

.picker-text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.picker-name,
.picker-sub {
  overflow-wrap: anywhere;
}

.picker-sub {
  font-size: var(--aha-size-sm);
  color: var(--aha-text-secondary);
}

.picker-empty {
  padding: var(--aha-space-12);
  font-size: var(--aha-size-sm);
  color: var(--aha-text-secondary);
}

.picker-errors {
  font-size: var(--aha-size-sm);
  color: var(--aha-text-negative);
}
</style>
